/**
 * Mehrere Dateien, EIN Vorgang, EINE Antwort. (23.09.2026, Ticket 2498)
 *
 * ## Woher das kommt
 *
 * Martin, 22.09.: zwei Sprachnachrichten von Albert, hochgeladen um 16:29
 * und 16:32. Jede ging als eigener Zug an Vespera, jede bekam ihre eigene
 * Antwort — die zweite begann mit „Ich lese dir die zweite Audiodatei
 * aus." Den Zusammenhang musste er um 16:37 selbst erfragen.
 *
 * Die Kopplung „eine Datei, eine Antwort" entstand im Browser, nicht am
 * Server: kein `multiple` am Dateifeld, nur `files[0]`, und jede Nicht-Bild-
 * Datei schickte sofort ihren eigenen /api/chat-Zug. /api/chat konnte schon
 * immer einen Zug mit Text, mehreren Adressen und bis zu fuenf Bildern.
 *
 * ## Was der Vorgang tut
 *
 *   1. EIN Dialog fuer alle Dateien: Auftrag und Projekt (Vorgabe: das
 *      aktive Projekt).
 *   2. Hochladen NACHEINANDER ueber das bestehende /api/upload — der Server
 *      haelt jede Datei ganz im Speicher (multer memoryStorage), parallel
 *      vervielfacht sich das.
 *   3. Jede Nicht-Bild-Datei einmal lesen lassen (POST
 *      /api/uploads/:id/lesen). So steht je Datei „fertig" oder „Fehler" da,
 *      und lange Aufnahmen sind erkannt, bevor Vespera antwortet.
 *   4. EIN Auftrag an Vespera, der alle Dateien nummeriert nennt und
 *      ausdruecklich eine gemeinsame Auswertung verlangt. Bilder gehen als
 *      Bilder mit.
 *
 * Eine gescheiterte Datei haelt die anderen nicht auf; sie steht mit Grund
 * auf der Karte UND im Auftrag, damit Vespera sie nicht verschweigt.
 *
 * Eine einzelne Datei laeuft wie bisher (app.ts, uploadAndAttachAnyFile),
 * nur Bilder ebenso (nimmBilder, das Tablett). Die Regel dafuer steht hier:
 * istDateiVorgang. Der Einzel-Dialog bietet aber „Weitere hinzufügen" — am
 * iPhone kommt eine Auswahl nur aus EINER Quelle, „1 PDF + Fotos" entsteht
 * also erst im zweiten Schritt und wird dann hier ein Vorgang.
 */
import {
  MAX_BILDER_JE_ZUG,
  MAX_DATEIEN_JE_VORGANG,
  MAX_GENERIC_UPLOAD_BYTES,
  MAX_IMAGE_UPLOAD_BYTES,
  SERVER_ANNAHME_MB,
} from "../../shared/limits.js";
import {
  askFileCaption,
  formatSize,
  type VorgangDateiAnzeige,
  type VorgangProjekt,
} from "./file-caption-modal.js";
import type { FileDto } from "./file-types.js";
import { getActiveProject, linkToProject, listProjects } from "./projects-api.js";
import { selectSheet } from "./sheet-ui.js";
import { zeichen } from "./zeichen.js";

/** Hochladen bei schwachem Netz: zwei Minuten je Datei. */
const HOCHLADEN_MS = 120_000;
/** Lesen: eine lange Aufnahme wird erkannt — bis zehn Minuten je Datei. */
const LESEN_MS = 600_000;

type VorgangStand =
  | "wartet"
  | "laedt"
  | "hochgeladen"
  | "liest"
  | "fertig"
  | "fehler";

export interface VorgangEintrag {
  /** 1-basiert — dieselbe Nummer steht auf der Karte und im Auftrag. */
  nr: number;
  name: string;
  groesse: number;
  mime: string;
  stand: VorgangStand;
  /** Warum es nicht ging — woertlich auf Karte und in den Auftrag. */
  grund?: string;
  /** Wie viel Text die Datei ergab. */
  zeichen?: number;
  /** Nur bei einem ZIP: wie viele Dateien darin stehen (Ticket 2580). */
  dateien?: number;
  url?: string;
  id?: string;
  /** Nummer unter den Bildern dieses Zuges („Bild 2:" in chat.ts). */
  bildNr?: number;
}

/**
 * Der EINE Zug an Vespera. `vorgang: true` nimmt ihn am Server vom
 * Beleg-Weg aus (istBelegUpload in server/chat.ts): der Auftrag nennt alle
 * Dateinamen, und „Rechnung.pdf" neben einem Foto machte sonst den ganzen
 * Vorgang zum Beleg.
 */
export interface VorgangsZug {
  message: string;
  imageUrls: string[];
  vorgang: true;
}

/** Was der Vorgang aus app.ts braucht — gereicht, nicht kopiert. */
export interface VorgangsUmgebung {
  /** DATEI_MARKER: geht ans Modell, wird aber nicht als Blase gezeichnet. */
  marker: string;
  artLabel: (mime: string) => string;
  verkleinern: (f: File) => Promise<Blob>;
  karteZeigen: (el: HTMLElement) => void;
  imAnruf: () => boolean;
  /** Der Rückgabewert (angekommen?) zählt hier nicht. */
  senden: (zug: VorgangsZug) => Promise<unknown>;
  systemZeile: (text: string) => void;
  /** Fuer den Verlauf — und damit der Abgleich die Bilder wiedererkennt. */
  merken: (hochgeladen: FileDto[]) => void;
  /** accept des Dateifelds, fuer „Weitere hinzufügen". */
  annehmen: () => string;
}

const STAND_TEXT: Record<VorgangStand, string> = {
  wartet: "wartet",
  laedt: "lädt hoch",
  hochgeladen: "hochgeladen",
  liest: "wird gelesen",
  fertig: "fertig",
  fehler: "Fehler",
};

function grundVon(e: VorgangEintrag): string {
  return e.grund ?? "unbekannter Fehler";
}

function istBildTyp(mime: string): boolean {
  return (mime || "").startsWith("image/");
}

function mb(bytes: number): number {
  return Math.round(bytes / 1024 / 1024);
}

function toast(message: string): void {
  window.dispatchEvent(
    new CustomEvent("voice-v8:toast", { detail: { message, kind: "info" } }),
  );
}

/**
 * Wird aus diesen Dateien ein Vorgang? (Die Regel an EINER Stelle.)
 *
 * Ja, sobald es mindestens zwei sind und mindestens eine davon kein Bild.
 * Eine einzelne Datei behaelt ihren Weg samt Dialog. Nur Bilder gehen wie
 * bisher ins Tablett — das kann seit dem 28.08. schon mehrere in EINEM Zug,
 * dort wird Text dazugeschrieben wie gewohnt.
 */
export function istDateiVorgang(dateien: ReadonlyArray<{ type: string }>): boolean {
  return dateien.length >= 2 && dateien.some((f) => !istBildTyp(f.type));
}

/**
 * Was vor dem Hochladen schon feststeht: Nummer, Name, und ob die Datei
 * ueberhaupt passt. Passt eine nicht, steht sie mit Grund da — die anderen
 * laufen weiter.
 */
export function pruefeVorab(dateien: ReadonlyArray<File>): VorgangEintrag[] {
  let bilder = 0;
  return dateien.map((f, i) => {
    const e: VorgangEintrag = {
      nr: i + 1,
      name: f.name || `Datei ${i + 1}`,
      groesse: f.size,
      mime: f.type || "application/octet-stream",
      stand: "wartet",
    };
    if (istBildTyp(f.type)) {
      if (f.size > MAX_IMAGE_UPLOAD_BYTES) {
        return { ...e, stand: "fehler", grund: `Bild größer als ${mb(MAX_IMAGE_UPLOAD_BYTES)} MB` };
      }
      if (bilder >= MAX_BILDER_JE_ZUG) {
        return {
          ...e,
          stand: "fehler",
          grund: `mehr als ${MAX_BILDER_JE_ZUG} Bilder sieht Vespera in einem Zug nicht`,
        };
      }
      bilder++;
    } else if (f.size > MAX_GENERIC_UPLOAD_BYTES) {
      return { ...e, stand: "fehler", grund: `größer als ${mb(MAX_GENERIC_UPLOAD_BYTES)} MB` };
    }
    return e;
  });
}

/**
 * Der EINE Auftrag an Vespera. Rein, damit pruefbar.
 *
 * ⚠️ Die Nummern der Bilder muessen zu „Bild n:" passen, das
 *    buildMultimodalContent (server/chat.ts) vor jedes Bild setzt — und das
 *    zaehlt nur die Bilder, die mitgehen. Darum kommt `bildNr` vom Aufrufer,
 *    in der Reihenfolge der imageUrls.
 */
export function baueVorgangsAuftrag(opts: {
  eintraege: ReadonlyArray<VorgangEintrag>;
  auftrag: string;
  projekt: string | null;
  artLabel: (mime: string) => string;
}): string {
  const { eintraege, artLabel } = opts;
  const n = eintraege.length;
  const bilderImZug = eintraege.filter((e) => e.bildNr !== undefined).length;
  const zeilen = eintraege.map((e) => {
    const kopf = `${e.nr}. ${e.name}`;
    if (!e.url) {
      return `${kopf} — nicht angekommen: ${grundVon(e)}. Sag das dem Nutzer ausdrücklich.`;
    }
    const adresse = `${e.url} (id ${e.id ?? "?"})`;
    if (e.bildNr !== undefined) {
      const bild = bilderImZug > 1 ? `Bild ${e.bildNr}` : "das Bild";
      return `${kopf} — ${bild}, siehst du direkt: ${adresse}`;
    }
    if (e.stand === "fehler") {
      return (
        `${kopf} — ${artLabel(e.mime)}: ${adresse} — beim Lesen gescheitert: ` +
        `${grundVon(e)}. Versuch read_file noch einmal; ` +
        `klappt es nicht, sag es dem Nutzer ausdrücklich.`
      );
    }
    return `${kopf} — ${artLabel(e.mime)}: ${adresse}`;
  });
  const auftrag = opts.auftrag.trim() || "Werte diese Dateien gemeinsam aus.";
  return [
    auftrag,
    "",
    `[Das ist EIN Vorgang mit ${n} Dateien. Lies jede lesbare Datei ` +
      `vollständig mit read_file (bei weiter_ab weiterlesen), bevor du ` +
      `antwortest. Werte sie GEMEINSAM aus: den roten Faden über alle ` +
      `Dateien, Widersprüche und Wiederholungen zwischen ihnen und das, was ` +
      `erst im Zusammenhang sichtbar wird — keine Einzelberichte ` +
      `hintereinander. Nenne die Nummer, wenn du dich auf eine Datei beziehst.`,
    ...zeilen,
    ...(opts.projekt ? [`Die Dateien liegen im Projekt „${opts.projekt}“.`] : []),
  ].join("\n") + "]";
}

// --- Die Karte im Chat --------------------------------------------------

interface KartenZustand {
  eintraege: VorgangEintrag[];
  auftrag: string;
  projekt: VorgangProjekt | null;
  /** Wie viele Dateien sich nicht zuordnen liessen. */
  projektFehler: number;
  fertig: boolean;
  imAnruf: boolean;
}

interface Karte {
  el: HTMLElement;
  zeichne: () => void;
}

function baueKarte(z: KartenZustand, u: VorgangsUmgebung): Karte {
  const n = z.eintraege.length;
  const el = document.createElement("div");
  el.className = "bubble user datei-vorgang";
  el.setAttribute("role", "group");
  el.setAttribute("aria-label", `${n} Dateien, ein Vorgang`);

  const kopf = document.createElement("div");
  kopf.className = "datei-vorgang-kopf";
  const titel = document.createElement("span");
  titel.className = "datei-vorgang-titel";
  titel.textContent = `${n} Dateien`;
  const summe = document.createElement("span");
  summe.className = "datei-vorgang-summe";
  kopf.append(titel, summe);

  const liste = document.createElement("ol");
  liste.className = "datei-vorgang-liste";
  liste.setAttribute("aria-live", "polite");
  const zeilen = z.eintraege.map((e) => {
    const li = document.createElement("li");
    li.className = "datei-vorgang-zeile";
    const nr = document.createElement("span");
    nr.className = "datei-vorgang-nr";
    nr.textContent = String(e.nr);
    nr.setAttribute("aria-hidden", "true");
    const text = document.createElement("span");
    text.className = "datei-vorgang-text";
    const name = document.createElement("span");
    name.className = "datei-vorgang-name";
    name.textContent = e.name;
    name.title = e.name;
    const sub = document.createElement("span");
    sub.className = "datei-vorgang-sub";
    text.append(name, sub);
    const stand = document.createElement("span");
    stand.className = "datei-vorgang-stand";
    li.append(nr, text, stand);
    liste.appendChild(li);
    return { li, sub, stand };
  });

  const auftrag = document.createElement("div");
  auftrag.className = "datei-vorgang-auftrag";
  auftrag.textContent = z.auftrag || "Gemeinsam auswerten";

  const fuss = document.createElement("div");
  fuss.className = "datei-vorgang-fuss";
  const projektZeile = document.createElement("span");
  projektZeile.className = "datei-vorgang-projekt";
  const projektKnopf = document.createElement("button");
  projektKnopf.type = "button";
  projektKnopf.className = "btn btn--ghost btn--sm datei-vorgang-projekt-knopf";
  projektKnopf.textContent = "Zu Projekt …";
  projektKnopf.addEventListener("click", () => {
    void zuProjektDanach(z, zeichne);
  });
  fuss.append(projektZeile, projektKnopf);

  el.append(kopf, liste, auftrag, fuss);

  function zeichne(): void {
    z.eintraege.forEach((e, i) => {
      const r = zeilen[i];
      if (!r) return;
      r.li.dataset.stand = e.stand;
      const art = u.artLabel(e.mime);
      r.sub.textContent =
        e.stand === "fehler"
          ? grundVon(e)
          : e.stand === "fertig" && e.dateien !== undefined
            ? `${formatSize(e.groesse)} · ${art} · ${e.dateien.toLocaleString("de-AT")} ${e.dateien === 1 ? "Datei" : "Dateien"}`
            : e.stand === "fertig" && e.zeichen !== undefined && !istBildTyp(e.mime)
              ? `${formatSize(e.groesse)} · ${art} · ${e.zeichen.toLocaleString("de-AT")} Zeichen`
              : `${formatSize(e.groesse)} · ${art}`;
      r.stand.className = `datei-vorgang-stand datei-vorgang-stand--${e.stand}`;
      r.stand.innerHTML =
        e.stand === "fertig"
          ? zeichen("haken", 12)
          : e.stand === "fehler"
            ? zeichen("fehler", 12)
            : "";
      r.stand.append(STAND_TEXT[e.stand]);
    });
    const fertig = z.eintraege.filter((e) => e.stand === "fertig").length;
    summe.textContent =
      z.fertig && z.imAnruf
        ? "abgelegt, nicht gelesen"
        : `${fertig} von ${n} fertig`;
    projektZeile.textContent = z.projekt
      ? `Im Projekt „${z.projekt.name}“` +
        (z.projektFehler > 0
          ? ` — ${z.projektFehler} ließ${z.projektFehler === 1 ? "" : "en"} sich nicht zuordnen`
          : "")
      : "Keinem Projekt zugeordnet";
    projektZeile.classList.toggle("is-fehler", z.projektFehler > 0);
    el.classList.toggle("is-fertig", z.fertig);
    // Zuordnen DANACH erst, wenn alles hochgeladen ist — vorher gibt es
    // nicht zu jeder Datei eine Kennung.
    projektKnopf.hidden = !z.fertig || !z.eintraege.some((e) => e.id);
  }
  zeichne();
  return { el, zeichne };
}

/** Alle hochgeladenen Dateien einem Projekt zuordnen; zaehlt Fehlschlaege. */
async function verknuepfe(eintraege: ReadonlyArray<VorgangEintrag>, projektId: string): Promise<number> {
  let fehl = 0;
  for (const e of eintraege) {
    if (!e.id) continue;
    try {
      // Idempotent (INSERT OR IGNORE, memory/projects.ts) — zweimal schadet nicht.
      await linkToProject(projektId, "upload", e.id);
    } catch {
      fehl++;
    }
  }
  return fehl;
}

/** „Zu Projekt …" auf der Karte — dieselbe Wahl wie in der Mediathek. */
async function zuProjektDanach(z: KartenZustand, zeichne: () => void): Promise<void> {
  const projekte = await listProjects().catch(() => []);
  if (projekte.length === 0) {
    toast("Noch keine Projekte vorhanden.");
    return;
  }
  const wahl = await selectSheet({
    title: "Zu welchem Projekt?",
    options: projekte.map((p) => ({ value: p.id, label: p.name })),
    ...(z.projekt ? { aktiv: z.projekt.id } : {}),
  });
  const p = projekte.find((x) => x.id === wahl);
  if (!p) return;
  z.projektFehler = await verknuepfe(z.eintraege, p.id);
  z.projekt = { id: p.id, name: p.name };
  zeichne();
}

// --- Hochladen und Lesen --------------------------------------------------

async function uploadGrund(res: Response): Promise<string> {
  if (res.status === 413) {
    return `zu groß für den Server (mehr als ${SERVER_ANNAHME_MB} MB)`;
  }
  let text = "";
  try {
    text = await res.text();
  } catch {
    /* ohne Text bleibt der Status */
  }
  try {
    const j = JSON.parse(text) as { error?: unknown };
    if (typeof j.error === "string" && j.error) {
      if (/mime not allowed/i.test(j.error)) return "dieses Format nimmt der Server nicht an";
      if (/too large/i.test(j.error)) return `größer als ${mb(MAX_GENERIC_UPLOAD_BYTES)} MB`;
      return `Server: ${j.error.slice(0, 120)}`;
    }
  } catch {
    /* keine JSON-Antwort, etwa eine HTML-Fehlerseite — nicht roh zeigen */
  }
  return `Hochladen fehlgeschlagen (HTTP ${res.status})`;
}

async function ladeHoch(
  f: File,
  e: VorgangEintrag,
  u: VorgangsUmgebung,
): Promise<FileDto | null> {
  const blob = istBildTyp(f.type) ? await u.verkleinern(f) : f;
  const fd = new FormData();
  // Der Name bleibt der echte: die Endung legt der Server nach dem Typ fest
  // (MIME_EXT in server/index.ts), ein verkleinertes Foto ist image/jpeg.
  fd.append("file", blob, f.name || e.name);
  // Der echte Name als Beschriftung: FileDto kennt ihn sonst nicht, und fuenf
  // Aufnahmen von Albert hiessen in Mediathek und Projekt fuenfmal
  // „Audio-Datei". Der Auftrag gehoert zum Vorgang, nicht zur Datei.
  fd.append("caption", (f.name || e.name).slice(0, 200));
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), HOCHLADEN_MS);
  try {
    const res = await fetch("/api/upload", {
      method: "POST",
      credentials: "same-origin",
      body: fd,
      signal: ac.signal,
    });
    if (!res.ok) {
      e.stand = "fehler";
      e.grund = await uploadGrund(res);
      return null;
    }
    const dto = (await res.json()) as FileDto;
    e.id = dto.id;
    e.url = dto.url;
    e.mime = dto.mime || e.mime;
    e.stand = "hochgeladen";
    return dto;
  } catch (err) {
    e.stand = "fehler";
    e.grund =
      err instanceof Error && err.name === "AbortError"
        ? "Zeitüberschreitung beim Hochladen — Netz zu langsam?"
        : `Hochladen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
    return null;
  } finally {
    clearTimeout(t);
  }
}

async function liesVorab(e: VorgangEintrag): Promise<void> {
  if (!e.id) return;
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), LESEN_MS);
  try {
    const res = await fetch(`/api/uploads/${encodeURIComponent(e.id)}/lesen`, {
      method: "POST",
      credentials: "same-origin",
      signal: ac.signal,
    });
    let j: { ok?: boolean; zeichen?: number; dateien?: number; fehler?: string; error?: string } = {};
    try {
      j = (await res.json()) as typeof j;
    } catch {
      /* keine JSON-Antwort */
    }
    if (res.ok && j.ok) {
      e.stand = "fertig";
      e.zeichen = typeof j.zeichen === "number" ? j.zeichen : 0;
      if (typeof j.dateien === "number") e.dateien = j.dateien;
      return;
    }
    e.stand = "fehler";
    e.grund = j.fehler ?? j.error ?? `Lesen fehlgeschlagen (HTTP ${res.status})`;
  } catch (err) {
    e.stand = "fehler";
    e.grund =
      err instanceof Error && err.name === "AbortError"
        ? "Lesen dauerte länger als 10 Minuten"
        : `Lesen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    clearTimeout(t);
  }
}

/**
 * „Weitere hinzufügen": ein eigenes, kurzlebiges Dateifeld — das feste im
 * Plus-Menue startet bei `change` einen NEUEN Vorgang. Auch der Einzel-Dialog
 * in app.ts (uploadAndAttachAnyFile) waehlt hierueber.
 *
 * ⚠️ click() laeuft synchron im Aufruf, also noch in der Geste des Knopfs.
 */
export function waehleWeitere(annehmen: string): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = annehmen;
    input.hidden = true;
    const fertig = (f: File[]): void => {
      input.remove();
      resolve(f);
    };
    input.addEventListener("change", () => fertig(Array.from(input.files ?? [])), { once: true });
    input.addEventListener("cancel", () => fertig([]), { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

function anzeige(dateien: ReadonlyArray<File>): VorgangDateiAnzeige[] {
  return dateien.map((f) => ({ name: f.name, groesse: f.size }));
}

async function aktivesProjekt(): Promise<VorgangProjekt | null> {
  try {
    const p = await getActiveProject();
    return p ? { id: p.id, name: p.name } : null;
  } catch {
    return null;
  }
}

/**
 * Der Vorgang: ein Dialog, nacheinander hochladen und lesen, eine Antwort.
 *
 * `auftrag`: was schon im Einzel-Dialog stand, bevor dort „Weitere
 * hinzufügen" daraus einen Vorgang machte — es geht nicht verloren.
 */
export async function starteDateiVorgang(
  dateien: ReadonlyArray<File>,
  u: VorgangsUmgebung,
  auftrag = "",
): Promise<void> {
  const liste = dateien.slice(0, MAX_DATEIEN_JE_VORGANG);
  if (dateien.length > MAX_DATEIEN_JE_VORGANG) {
    toast(
      `Ich nehme ${MAX_DATEIEN_JE_VORGANG} von ${dateien.length} — mehr passen nicht in einen Vorgang.`,
    );
  }
  if (liste.length === 0) return;

  const antwort = await askFileCaption({
    filename: `${liste.length} Dateien`,
    sizeBytes: liste.reduce((s, f) => s + f.size, 0),
    mimeType: "",
    defaultCaption: auftrag,
    vorgang: {
      dateien: anzeige(liste),
      projektVorgabe: await aktivesProjekt(),
      projekteLaden: async () =>
        (await listProjects()).map((p) => ({ id: p.id, name: p.name })),
      weitere: async () => {
        const neu = await waehleWeitere(u.annehmen());
        const platz = Math.max(0, MAX_DATEIEN_JE_VORGANG - liste.length);
        if (neu.length > platz) {
          toast(
            `Ich nehme ${platz} von ${neu.length} — mehr als ${MAX_DATEIEN_JE_VORGANG} passen nicht in einen Vorgang.`,
          );
        }
        liste.push(...neu.slice(0, platz));
        return anzeige(liste);
      },
      hoechstens: MAX_DATEIEN_JE_VORGANG,
    },
  });
  if (antwort.cancelled) return;

  const imAnruf = u.imAnruf();
  const z: KartenZustand = {
    eintraege: pruefeVorab(liste),
    auftrag: antwort.caption.trim(),
    projekt: antwort.projekt ?? null,
    projektFehler: 0,
    fertig: false,
    imAnruf,
  };
  const karte = baueKarte(z, u);
  u.karteZeigen(karte.el);

  // 1) Nacheinander hochladen — und gleich dem Projekt zuordnen.
  const hochgeladen: FileDto[] = [];
  for (let i = 0; i < z.eintraege.length; i++) {
    const e = z.eintraege[i];
    const f = liste[i];
    if (!e || !f || e.stand !== "wartet") continue;
    e.stand = "laedt";
    karte.zeichne();
    const dto = await ladeHoch(f, e, u);
    if (dto) {
      hochgeladen.push(dto);
      if (z.projekt) z.projektFehler += await verknuepfe([e], z.projekt.id);
    }
    karte.zeichne();
  }

  // 2) Nacheinander lesen. Im Anruf nicht: dort kommt ohnehin nichts beim
  //    Modell an (siehe attachFileToChat in app.ts).
  if (!imAnruf) {
    for (const e of z.eintraege) {
      if (e.stand !== "hochgeladen") continue;
      if (istBildTyp(e.mime)) {
        e.stand = "fertig";
        continue;
      }
      e.stand = "liest";
      karte.zeichne();
      await liesVorab(e);
      karte.zeichne();
    }
  }

  u.merken(hochgeladen);
  z.fertig = true;
  karte.zeichne();

  const n = z.eintraege.length;
  if (imAnruf) {
    u.systemZeile(
      `Im Anruf kommen Dateien nicht bei Vespera an. Die ${n} Dateien liegen in der Mediathek — frag im Chat danach.`,
    );
    return;
  }
  const bereit = z.eintraege.filter((e) => e.stand === "fertig");
  if (bereit.length === 0) {
    u.systemZeile(`Keine der ${n} Dateien ließ sich lesen — Vespera bekommt diesmal nichts.`);
    return;
  }
  // Bildnummern in der Reihenfolge, in der die Bilder in den Zug gehen.
  const bilder = bereit.filter((e) => istBildTyp(e.mime) && e.url);
  bilder.forEach((e, i) => {
    e.bildNr = i + 1;
  });
  await u.senden({
    message:
      u.marker +
      baueVorgangsAuftrag({
        eintraege: z.eintraege,
        auftrag: z.auftrag,
        projekt: z.projekt?.name ?? null,
        artLabel: u.artLabel,
      }),
    imageUrls: bilder.map((e) => e.url as string),
    vorgang: true,
  });
}
